/**
 * The tradition (lineage) filter, raised as a bottom sheet from the control
 * inside the search field. A sheet rather than the old inline panel: it is
 * where any further advanced filters will live, and on a phone a sheet is the
 * expected home for them. Selections apply live — the footer button only
 * dismisses the sheet and reports the current result count.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';

export default function FilterSheet({
  open,
  onClose,
  lineages,
  activeLineages,
  toggleLineage,
  clearLineages,
  resultCount,
}) {
  const { t } = useTranslation();
  const closeRef = useRef(null);
  const isFiltered = activeLineages.length > 0;

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    // Stop the grid behind the sheet scrolling under a thumb while it is open.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="sheet-scrim" role="presentation" onClick={onClose}>
      <div
        className="filter-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="filter-sheet-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="filter-sheet__head">
          <h2 id="filter-sheet-title" className="filter-sheet__title">
            {t('explore.filterSheetTitle', 'Filter by tradition')}
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="filter-sheet__close"
            onClick={onClose}
            aria-label={t('explore.filterClose', 'Close filters')}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <div className="filter-sheet__body">
          {lineages.map((lineage) => {
            const on = activeLineages.includes(lineage.key);
            return (
              <button
                key={lineage.key}
                type="button"
                className={`filter-opt${on ? ' is-on' : ''}`}
                aria-pressed={on}
                onClick={() => toggleLineage(lineage.key)}
              >
                <span className="filter-opt__check" aria-hidden="true">
                  {on && <Icon name="check" size={14} />}
                </span>
                <span className="filter-opt__text">
                  <span className="filter-opt__name">{lineage.name}</span>
                  {lineage.subtitle && (
                    <span className="filter-opt__sub">{lineage.subtitle}</span>
                  )}
                </span>
                <span className="filter-opt__count">{lineage.monasteryCount}</span>
              </button>
            );
          })}
        </div>

        <div className="filter-sheet__foot">
          <button
            type="button"
            className="btn btn--quiet"
            onClick={clearLineages}
            disabled={!isFiltered}
          >
            {t('explore.showAll', 'Show all')}
          </button>
          <button type="button" className="btn btn--primary" onClick={onClose}>
            {t('explore.filterApply', {
              count: resultCount,
              defaultValue: `Show ${resultCount} ${resultCount === 1 ? 'monastery' : 'monasteries'}`,
            })}
          </button>
        </div>
      </div>
    </div>
  );
}
